import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/components/Chat.css";
import API_BASE, { withApiCredentials } from "../api";
import AppBackButton from "./common/AppBackButton";
import { getLegacyAppToken } from "../auth/bootstrapTokens";
import { trackProductEvent } from "../analytics/trackEvent";
import {
  getCatchSubmitRefreshEventName,
  reportBackgroundRefreshBlocked,
  shouldBlockBackgroundRefresh,
} from "../app/catchSubmitGate";

const MESSAGE_REFRESH_EVENT = "messagesUpdated";
const MESSAGE_REFRESH_INTERVAL_MS = 5000;

function normalizeMessageTimestamp(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return numericValue < 1e12 ? numericValue * 1000 : numericValue;
}

function formatMessageTimestamp(value) {
  const timestamp = normalizeMessageTimestamp(value);
  if (timestamp == null) return "";

  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getMessageDateKey(value) {
  const timestamp = normalizeMessageTimestamp(value);
  if (timestamp == null) return "";

  const date = new Date(timestamp);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function formatMessageDateLabel(value) {
  const timestamp = normalizeMessageTimestamp(value);
  if (timestamp == null) return "";

  return new Date(timestamp).toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function normalizeBackButtonLabel(label) {
  return String(label || "")
    .replace(/^\s*(?:←|<-|⟵|⟸|↞|↤)\s*/, "")
    .trim();
}

function resolveMediaUrl(mediaUrl) {
  if (!mediaUrl) return "";
  return /^https?:\/\//i.test(mediaUrl) ? mediaUrl : `${API_BASE}${mediaUrl}`;
}

function isVideoMediaUrl(mediaUrl) {
  const normalized = (mediaUrl || "").split("?")[0].toLowerCase();
  return [".mp4", ".webm", ".ogg", ".mov", ".m4v", ".avi", ".mkv"].some((ext) =>
    normalized.endsWith(ext)
  );
}

async function fetchConversationMessages(token, userId) {
  if (!userId) return null;

  const res = await fetch(
    `${API_BASE}/messages/conversation?with_user_id=${userId}`,
    withApiCredentials({
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
    })
  );

  if (!res.ok) return null;

  return (await res.json()) || [];
}

async function markConversationAsRead(token, userId) {
  if (!userId) return;

  const fd = new FormData();
  if (token) {
    fd.append("token", token);
  }
  fd.append("with_user_id", userId);

  await fetch(`${API_BASE}/messages/mark-read`, withApiCredentials({
    method: "POST",
    body: fd,
  }));

  window.dispatchEvent(new Event("messagesRead"));
}

function mergeMessagesById(existingMessages, nextMessages) {
  const merged = new Map();

  existingMessages.forEach((message) => {
    if (message?.id) {
      merged.set(message.id, message);
    }
  });

  nextMessages.forEach((message) => {
    if (message?.id) {
      merged.set(message.id, message);
    }
  });

  return Array.from(merged.values()).sort((left, right) => {
    const leftTimestamp = normalizeMessageTimestamp(left?.createdAt) || 0;
    const rightTimestamp = normalizeMessageTimestamp(right?.createdAt) || 0;
    if (leftTimestamp !== rightTimestamp) {
      return leftTimestamp - rightTimestamp;
    }
    return String(left?.id || "").localeCompare(String(right?.id || ""));
  });
}

export default function Chat({
  currentUser = null,
  userId,
  userName = "",
  sharedText = "",
  onBack,
  onOpenSharedPost,
  hideBackButton = false,
  headerActions = null,
}) {
  const token = getLegacyAppToken(window);
  const { t } = useTranslation();
  const currentUserId = String(currentUser?.id || "").trim();
  const hasAuthenticatedUser = Boolean(token || currentUserId);
  const backButtonLabel = normalizeBackButtonLabel(
    t("back", { defaultValue: "Back" })
  );

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [myUserId, setMyUserId] = useState(null);
  const [otherUserName, setOtherUserName] = useState("");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaPreviewUrl, setSelectedMediaPreviewUrl] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isConversationLoading, setIsConversationLoading] = useState(
    Boolean(hasAuthenticatedUser && userId)
  );
  const [conversationError, setConversationError] = useState("");

  const bottomRef = useRef(null);
  const mediaInputRef = useRef(null);
  const composerInputRef = useRef(null);
  const messagesRef = useRef([]);
  const conversationRequestIdRef = useRef(0);
  const conversationInFlightRef = useRef(false);
  const queuedConversationRefreshRef = useRef(null);
  const activeConversationPromiseRef = useRef(null);
  const markReadInFlightRef = useRef(false);
  const queuedMarkReadRef = useRef(false);
  const lastMarkedUnreadSignatureRef = useRef("");
  const chatOpenTrackedForUserRef = useRef("");

  useEffect(() => {
    if (!hasAuthenticatedUser || !userId || chatOpenTrackedForUserRef.current === userId) {
      return;
    }

    chatOpenTrackedForUserRef.current = userId;
    trackProductEvent("chat_opened", {
      featureArea: "messages",
      metadata: { source: "chat_window" },
    });
  }, [hasAuthenticatedUser, userId]);

  // ============================
  // Prefill text when shared from post
  // ============================
  useEffect(() => {
    if (sharedText) {
      setText(sharedText);
    }
  }, [sharedText]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ============================
  // Load my user ID
  // ============================
  useEffect(() => {
    if (currentUserId) {
      setMyUserId(currentUserId);
      return;
    }

    if (!hasAuthenticatedUser) {
      setMyUserId(null);
      return;
    }

    fetch(
      `${API_BASE}/auth/me`,
      withApiCredentials({
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      })
    )
      .then((res) => res.json())
      .then((me) => setMyUserId(me.id))
      .catch(() => {});
  }, [currentUserId, hasAuthenticatedUser, token]);

  // ============================
  // Load chat partner name
  // ============================
  useEffect(() => {
    if (!userId) {
      setOtherUserName("");
      return;
    }

    if (userName) {
      setOtherUserName(userName);
      return;
    }

    const controller = new AbortController();

    async function loadOtherUserName() {
      try {
        const res = await fetch(`${API_BASE}/auth/users`, {
          signal: controller.signal,
        });
        if (!res.ok) return;

        const users = await res.json();
        const otherUser = users.find((user) => user.id === userId);
        setOtherUserName(otherUser?.username || "");
      } catch (err) {
        if (err.name !== "AbortError") {
          setOtherUserName("");
        }
      }
    }

    loadOtherUserName();
    return () => controller.abort();
  }, [userId, userName]);

  // ============================
  // Load conversation
  // ============================
  const loadConversation = useCallback(
    async ({ showLoader = false } = {}) => {
      if (!hasAuthenticatedUser || !userId) {
        setMessages([]);
        setConversationError("");
        setIsConversationLoading(false);
        lastMarkedUnreadSignatureRef.current = "";
        return null;
      }

      const requestId = conversationRequestIdRef.current + 1;
      conversationRequestIdRef.current = requestId;

      if (showLoader) {
        setConversationError("");
        if (messagesRef.current.length === 0) {
          setIsConversationLoading(true);
        }
      }

      try {
        const data = await fetchConversationMessages(token, userId);
        if (!data) {
          if (showLoader && requestId === conversationRequestIdRef.current) {
            setConversationError(
              t("conversation_load_failed", {
                defaultValue: "Failed to load conversation.",
              })
            );
          }
          return null;
        }

        if (requestId !== conversationRequestIdRef.current) {
          return null;
        }

        setMessages(data);
        setConversationError("");
        return data;
      } catch (err) {
        if (showLoader && requestId === conversationRequestIdRef.current) {
          setConversationError(
            t("conversation_load_failed", {
              defaultValue: "Failed to load conversation.",
            })
          );
        }
        return null;
      } finally {
        if (showLoader && requestId === conversationRequestIdRef.current) {
          setIsConversationLoading(false);
        }
      }
    },
    [hasAuthenticatedUser, token, userId, t]
  );

  // ============================
  // Mark messages as read
  // ============================
  const markAsRead = useCallback(async () => {
    return markConversationAsRead(token, userId);

    // ðŸ”” Tell navbar to refresh unread count
  }, [token, userId]);

  const maybeMarkConversationAsRead = useCallback(
    async (conversationMessages) => {
      if (!hasAuthenticatedUser || !userId || !myUserId || !Array.isArray(conversationMessages)) {
        return;
      }

      const unreadIncomingMessages = conversationMessages.filter(
        (message) => message?.toUserId === myUserId && !message?.read
      );

      if (unreadIncomingMessages.length === 0) {
        lastMarkedUnreadSignatureRef.current = "";
        return;
      }

      const unreadSignature = unreadIncomingMessages
        .map((message) => String(message?.id || ""))
        .filter(Boolean)
        .join("|");

      if (!unreadSignature || lastMarkedUnreadSignatureRef.current === unreadSignature) {
        return;
      }

      if (markReadInFlightRef.current) {
        queuedMarkReadRef.current = true;
        return;
      }

      markReadInFlightRef.current = true;
      lastMarkedUnreadSignatureRef.current = unreadSignature;

      try {
        await markAsRead();
      } catch (error) {
        lastMarkedUnreadSignatureRef.current = "";
        throw error;
      } finally {
        markReadInFlightRef.current = false;
        if (queuedMarkReadRef.current) {
          queuedMarkReadRef.current = false;
          maybeMarkConversationAsRead(conversationMessages).catch(() => {});
        }
      }
    },
    [hasAuthenticatedUser, markAsRead, myUserId, userId]
  );

  const refreshConversation = useCallback(
    ({ showLoader = false, markReadAfterLoad = false } = {}) => {
      if (!hasAuthenticatedUser || !userId) {
        return Promise.resolve(null);
      }
      if (shouldBlockBackgroundRefresh()) {
        reportBackgroundRefreshBlocked({ source: "messages_conversation" });
        return Promise.resolve(null);
      }

      if (conversationInFlightRef.current) {
        const queuedRefresh = queuedConversationRefreshRef.current || {
          showLoader: false,
          markReadAfterLoad: false,
        };
        queuedRefresh.showLoader = queuedRefresh.showLoader || showLoader;
        queuedRefresh.markReadAfterLoad =
          queuedRefresh.markReadAfterLoad || markReadAfterLoad;
        queuedConversationRefreshRef.current = queuedRefresh;
        return activeConversationPromiseRef.current || Promise.resolve(null);
      }

      conversationInFlightRef.current = true;

      const refreshPromise = loadConversation({ showLoader })
        .then(async (data) => {
          if (data && markReadAfterLoad) {
            await maybeMarkConversationAsRead(data);
          }
          return data;
        })
        .finally(() => {
          conversationInFlightRef.current = false;
          activeConversationPromiseRef.current = null;

          const queuedRefresh = queuedConversationRefreshRef.current;
          queuedConversationRefreshRef.current = null;
          if (queuedRefresh) {
            refreshConversation(queuedRefresh).catch(() => {});
          }
        });

      activeConversationPromiseRef.current = refreshPromise;
      return refreshPromise;
    },
    [hasAuthenticatedUser, loadConversation, maybeMarkConversationAsRead, userId]
  );

  useEffect(() => {
    setMessages([]);
    setConversationError("");
    setIsConversationLoading(Boolean(hasAuthenticatedUser && userId));
    lastMarkedUnreadSignatureRef.current = "";

    refreshConversation({
      showLoader: true,
      markReadAfterLoad: true,
    }).catch(() => {});

    return () => {
      conversationRequestIdRef.current += 1;
      conversationInFlightRef.current = false;
      queuedConversationRefreshRef.current = null;
      activeConversationPromiseRef.current = null;
      markReadInFlightRef.current = false;
      queuedMarkReadRef.current = false;
      lastMarkedUnreadSignatureRef.current = "";
    };
  }, [hasAuthenticatedUser, refreshConversation, userId]);

  useEffect(() => {
    if (!hasAuthenticatedUser || !userId) return undefined;

    const requestConversationRefresh = (options = {}) => {
      refreshConversation(options).catch(() => {});
    };

    const refreshVisibleConversation = () => {
      if (document.visibilityState === "visible") {
        requestConversationRefresh({ markReadAfterLoad: true });
      }
    };

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        requestConversationRefresh({ markReadAfterLoad: true });
      }
    }, MESSAGE_REFRESH_INTERVAL_MS);

    const handleMessagesUpdated = () => {
      requestConversationRefresh({ markReadAfterLoad: true });
    };
    const handleFocus = () => {
      requestConversationRefresh({ markReadAfterLoad: true });
    };

    const refreshEvent = getCatchSubmitRefreshEventName();
    window.addEventListener(MESSAGE_REFRESH_EVENT, handleMessagesUpdated);
    window.addEventListener(refreshEvent, handleMessagesUpdated);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", refreshVisibleConversation);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener(MESSAGE_REFRESH_EVENT, handleMessagesUpdated);
      window.removeEventListener(refreshEvent, handleMessagesUpdated);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", refreshVisibleConversation);
    };
  }, [hasAuthenticatedUser, refreshConversation, userId]);

  // ============================
  // Auto scroll
  // ============================
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!selectedMedia) {
      setSelectedMediaPreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(selectedMedia);
    setSelectedMediaPreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [selectedMedia]);

  useEffect(() => {
    const composer = composerInputRef.current;
    if (!composer) return;

    composer.style.height = "auto";
    composer.style.height = `${Math.min(composer.scrollHeight, 160)}px`;
    composer.style.overflowY = composer.scrollHeight > 160 ? "auto" : "hidden";
  }, [text]);

  // ============================
  // Send message
  // ============================
  const sendMessage = async () => {
    if (isSending || !hasAuthenticatedUser || !userId || (!text.trim() && !selectedMedia)) {
      return;
    }

    setIsSending(true);

    const fd = new FormData();
    if (token) {
      fd.append("token", token);
    }
    fd.append("to_user_id", userId);
    fd.append("text", text);
    if (selectedMedia) {
      fd.append("media", selectedMedia);
    }

    try {
      const res = await fetch(`${API_BASE}/messages/send`, withApiCredentials({
        method: "POST",
        body: fd,
      }));

      if (!res.ok) return;

      const createdMessage = await res.json();
      const nextMessages = mergeMessagesById(messages, [createdMessage]);
      trackProductEvent("message_sent", {
        featureArea: "messages",
        metadata: {
          source: "chat_composer",
          has_media: Boolean(selectedMedia),
          has_shared_post: Boolean(createdMessage?.sharedPost),
        },
      });
      setMessages(nextMessages);
      setText("");
      setSelectedMedia(null);
      if (mediaInputRef.current) {
        mediaInputRef.current.value = "";
      }

      window.dispatchEvent(new Event(MESSAGE_REFRESH_EVENT));
      await maybeMarkConversationAsRead(nextMessages);
      refreshConversation({ markReadAfterLoad: false }).catch(() => {});
    } finally {
      setIsSending(false);
    }
  };

  const handlePickMedia = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedMedia(file);
  };

  const handleRemoveMedia = () => {
    setSelectedMedia(null);
    if (mediaInputRef.current) {
      mediaInputRef.current.value = "";
    }
  };

  const openSharedPost = (sharedPost) => {
    if (!sharedPost?.id) return;
    if (typeof onOpenSharedPost !== "function") {
      return;
    }
    onOpenSharedPost(sharedPost);
  };

  return (
    <div className="chat-window">
      {/* HEADER */}
      <div className="chat-header">
        {!hideBackButton && (
          <AppBackButton
            className="chat-back"
            label={backButtonLabel}
            onClick={() => {
              setText("");
              onBack();
            }}
          />
        )}
        <span className="chat-title">{t("conversation")}</span>
        {headerActions ? (
          <div className="chat-header-actions">{headerActions}</div>
        ) : null}
      </div>

      {/* MESSAGES */}
      <div
        className={`chat-messages ${
          isConversationLoading || conversationError || messages.length === 0
            ? "has-state"
            : ""
        }`}
      >
        {isConversationLoading ? (
          <div className="chat-state">
            {t("loading", { defaultValue: "Loading..." })}
          </div>
        ) : conversationError ? (
          <div className="chat-state chat-state-error">{conversationError}</div>
        ) : messages.length === 0 ? (
          <div className="chat-state">
            {t("no_messages_yet", { defaultValue: "No messages yet" })}
          </div>
        ) : (
          messages.map((m, index) => {
          const isMine = m.fromUserId === myUserId;
          const senderLabel = isMine
            ? t("message_sender_me", { defaultValue: "Me" })
            : otherUserName || t("user");
          const currentDateKey = getMessageDateKey(m.createdAt);
          const previousDateKey = index > 0 ? getMessageDateKey(messages[index - 1]?.createdAt) : "";
          const showDateSeparator = Boolean(currentDateKey) && currentDateKey !== previousDateKey;
          const dateLabel = showDateSeparator ? formatMessageDateLabel(m.createdAt) : "";
          const timestampLabel = formatMessageTimestamp(m.createdAt);
          const noteText = (m.text || "").trim();
          const messageMediaUrl = resolveMediaUrl(m.mediaUrl);
          const messageMediaIsVideo =
            m.mediaType === "video" || isVideoMediaUrl(m.mediaUrl);
          const sharedPost = m.sharedPost;
          const sharedPostMediaUrl = resolveMediaUrl(sharedPost?.mediaUrl);
          const sharedPostIsVideo = isVideoMediaUrl(sharedPost?.mediaUrl);
          const statusLabel = isMine
            ? m.read
              ? t("message_status_seen", { defaultValue: "Seen" })
              : m.deliveredAt
                ? t("message_status_delivered", { defaultValue: "Delivered" })
                : t("message_status_sent", { defaultValue: "Sent" })
            : "";

          return (
            <React.Fragment key={m.id}>
              {showDateSeparator && (
                <div className="chat-date-separator">
                  <span>{dateLabel}</span>
                </div>
              )}
              <div
                className={`chat-message ${isMine ? "mine" : "theirs"}`}
              >
                <div className={`chat-message-content ${isMine ? "mine" : "theirs"}`}>
                  <div className={`chat-sender-label ${isMine ? "mine" : "theirs"}`}>
                    {senderLabel}
                  </div>
                  <div className={`chat-bubble ${isMine ? "mine" : "theirs"}`}>
                    {noteText ? <div className="chat-text">{noteText}</div> : null}

                    {messageMediaUrl ? (
                      messageMediaIsVideo ? (
                        <video
                          className={`chat-message-media${noteText ? " with-note" : ""}`}
                          src={messageMediaUrl}
                          controls
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          className={`chat-message-media${noteText ? " with-note" : ""}`}
                          src={messageMediaUrl}
                          alt={t("message_media_attachment", {
                            defaultValue: "Message attachment",
                          })}
                        />
                      )
                    ) : null}

                    {sharedPost ? (
                      <button
                        type="button"
                        className={`chat-shared-post-card ${isMine ? "mine" : "theirs"}${noteText ? " with-note" : ""}`}
                        onClick={() => openSharedPost(sharedPost)}
                      >
                        <div className="chat-shared-post-label">
                          {t("shared_post_label", { defaultValue: "Shared post" })}
                        </div>
                        <div className="chat-shared-post-author">
                          {sharedPost.user || t("user")}
                        </div>

                        {sharedPost.caption ? (
                          <div className="chat-shared-post-caption">
                            {sharedPost.caption}
                          </div>
                        ) : null}

                        {sharedPostMediaUrl ? (
                          sharedPostIsVideo ? (
                            <video
                              className="chat-shared-post-media"
                              src={sharedPostMediaUrl}
                              muted
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img
                              className="chat-shared-post-media"
                              src={sharedPostMediaUrl}
                              alt={t("shared_post_media", {
                                defaultValue: "Shared post media",
                              })}
                            />
                          )
                        ) : null}

                        <div className="chat-shared-post-footer">
                          <span className="chat-shared-post-open">
                            {t("open_post", { defaultValue: "Open post" })}
                          </span>
                        </div>
                      </button>
                    ) : null}

                    {(timestampLabel || statusLabel) && (
                      <div className={`chat-bubble-meta ${isMine ? "mine" : "theirs"}`}>
                        {timestampLabel && (
                          <span className="chat-time">{timestampLabel}</span>
                        )}
                        {statusLabel && (
                          <span className="chat-status">{statusLabel}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div className="chat-input">
        {selectedMediaPreviewUrl ? (
          <div className="chat-input-preview">
            {selectedMedia?.type?.startsWith("video/") ? (
              <video
                className="chat-input-preview-media"
                src={selectedMediaPreviewUrl}
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                className="chat-input-preview-media"
                src={selectedMediaPreviewUrl}
                alt={t("message_media_attachment", {
                  defaultValue: "Message attachment",
                })}
              />
            )}

            <button
              type="button"
              className="chat-input-preview-remove"
              onClick={handleRemoveMedia}
              disabled={isSending}
            >
              {t("remove", { defaultValue: "Remove" })}
            </button>
          </div>
        ) : null}

        <div className="chat-input-row">
          <textarea
            ref={composerInputRef}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isSending) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={t("type_message_placeholder")}
            disabled={isSending}
          />

          <input
            ref={mediaInputRef}
            type="file"
            accept="image/*,video/*"
            className="chat-attach-input"
            onChange={handlePickMedia}
            disabled={isSending}
          />

          <button
            type="button"
            className="chat-attach-btn"
            onClick={() => mediaInputRef.current?.click()}
            disabled={isSending}
          >
            {t("attach", { defaultValue: "Attach" })}
          </button>

          <button onClick={sendMessage} disabled={isSending}>
            {isSending ? t("loading", { defaultValue: "Loading..." }) : t("send")}
          </button>
        </div>

        {isSending ? (
          <div className="chat-input-status">
            {t("loading", { defaultValue: "Loading..." })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
